import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, ArrowLeft, Heart, Sparkles,
  Briefcase, BookOpen, Building2 as Building, Gamepad2, Dumbbell,
  Car, Hotel, Palette, Cpu, Factory, Users, Leaf,
  Utensils, ShoppingBag, AlertTriangle, SlidersHorizontal,
  Check, Search, HelpCircle, Lock,
} from "lucide-react";
import { createProfile, getProfileLimitDefaults, getProfileSlots, logout as apiLogout } from "../lib/api";
import { idLabel } from "../lib/idLabels";

function useEnterKey(callback) {
  useEffect(() => {
    function handler(e) {
      if (e.key !== "Enter") return;
      // Don't trigger if user is typing in textarea
      if (e.target.tagName === "TEXTAREA") return;
      e.preventDefault();
      callback();
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [callback]);
}
import { useAuth } from "../context/AuthContext";

const ACCENT = "#1d9160";
const PRIMARY = "#0c3124";

const ICON_MAP = {
  Utensils, ShoppingBag, Heart, Sparkles, Briefcase, BookOpen,
  Building, Gamepad2, Dumbbell, Car, Hotel, Palette, Cpu, Factory, Users, Leaf,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initAgeGender() {
  const keys = ["Male", "Female"].flatMap((g) =>
    ["<18", "18–24", "25–34", "35–44", "45+"].map((a) => `${g}|${a}`)
  );
  const result = {};
  keys.forEach((k) => { result[k] = 10; });
  return result;
}

function redistribute(segments, changedKey, newVal) {
  const clamped = Math.max(0, Math.min(100, newVal));
  const otherKeys = Object.keys(segments).filter((k) => k !== changedKey);
  const totalOthers = otherKeys.reduce((s, k) => s + segments[k], 0);
  const remaining = 100 - clamped;
  const next = { ...segments, [changedKey]: clamped };

  if (totalOthers === 0) {
    const each = remaining / otherKeys.length;
    otherKeys.forEach((k) => { next[k] = Math.floor(each); });
  } else {
    otherKeys.forEach((k) => {
      next[k] = Math.floor((segments[k] / totalOthers) * remaining);
    });
  }

  let diff = 100 - Object.values(next).reduce((s, v) => s + v, 0);
  const sorted = [...otherKeys].sort((a, b) => next[b] - next[a]);
  for (let i = 0; diff !== 0 && i < sorted.length; i++) {
    next[sorted[i]] += diff > 0 ? 1 : -1;
    diff += diff > 0 ? -1 : 1;
  }
  return next;
}

// ─── Primitives ───────────────────────────────────────────────────────────────

function Label({ children, hint, danger, mode }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 flex-wrap mb-0.5">
        <p className={`text-lg font-bold leading-snug ${danger ? "text-[#ba1a1a]" : "text-[#191c1d]"}`}>
          {children}
        </p>
        {mode === "multi" && (
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-[#104230]/10 text-[#104230]">
            Pilih Banyak
          </span>
        )}
        {mode === "single" && (
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-[#f3f4f5] text-[#71787b]">
            Pilih Satu
          </span>
        )}
      </div>
      {hint && (
        <p className="mt-1 text-sm leading-relaxed text-[#40484a]">{hint}</p>
      )}
    </div>
  );
}

function ChipGroup({ options, value, onChange, multi = false, danger = false }) {
  function toggle(v) {
    if (multi) onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    else onChange(v);
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const sel = multi ? value.includes(o) : value === o;
        const base = "px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-all duration-200 border";
        let cls, style = {};

        if (sel && danger) {
          cls = base;
          style = { background: "#ba1a1a", color: "#fff", borderColor: "#ba1a1a" };
        } else if (sel) {
          cls = base;
          style = { background: PRIMARY, color: "#fff", borderColor: PRIMARY };
        } else if (danger) {
          cls = `${base} bg-white text-[#40484a] border-[#c0c8ca] hover:border-[#ffb4ab] hover:text-[#ba1a1a]`;
        } else {
          cls = `${base} bg-white text-[#40484a] border-[#c0c8ca] hover:bg-[#f3f4f5]`;
        }

        return (
          <button key={o} onClick={() => toggle(o)} style={style} className={cls}>
            {danger && sel && <span className="mr-1">✕</span>}
            {idLabel(o)}
          </button>
        );
      })}
    </div>
  );
}

const RESOLUTION_OPTIONS = [
  { v: "100m", t: "100 m", d: "Presisi setingkat jalan · lebih banyak sel" },
  { v: "1km",  t: "1 km",  d: "Gambaran seluruh kota · cepat" },
];

// Population catchment: how far out the site-score counts residents. Precomputed
// (no API cost). Drives the Analysis grid score + the "Population within X km"
// read in Deep Analysis.
const CATCHMENT_OPTIONS = [
  { v: "1km",  t: "1 km",  d: "Hiperlokal" },
  { v: "3km",  t: "3 km",  d: "Lingkungan sekitar" },
  { v: "5km",  t: "5 km",  d: "Distrik lokal" },
  { v: "7km",  t: "7 km",  d: "Area yang lebih luas" },
  { v: "10km", t: "10 km", d: "Zona kota" },
  { v: "15km", t: "15 km", d: "Kawasan kota besar" },
  { v: "20km", t: "20 km", d: "Skala metro" },
  { v: "25km", t: "25 km", d: "Metro (luas)" },
  { v: "30km", t: "30 km", d: "Regional" },
  { v: "35km", t: "35 km", d: "Regional (luas)" },
  { v: "40km", t: "40 km", d: "Wilayah yang diperluas" },
];

function OptionCard({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const sel = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={sel ? { borderColor: PRIMARY, background: "#f1f5f3" } : {}}
            className={`rounded-2xl border px-4 py-3 text-left transition-all ${
              sel ? "" : "bg-white border-[#c0c8ca] hover:bg-[#f3f4f5]"
            }`}
          >
            <div className="text-sm font-bold" style={{ color: sel ? PRIMARY : "#1b1b1b" }}>{o.t}</div>
            <div className="text-[11px] text-[#6b7375] mt-0.5">{o.d}</div>
          </button>
        );
      })}
    </div>
  );
}

const ResolutionSelect = ({ value, onChange }) => (
  <OptionCard value={value} onChange={onChange} options={RESOLUTION_OPTIONS} />
);
const CatchmentSelect = ({ value, onChange }) => (
  <OptionCard value={value} onChange={onChange} options={CATCHMENT_OPTIONS} />
);

function StepBlock({ children }) {
  return (
    <section className="rounded-xl bg-[#f3f4f5] p-5 md:p-6">
      {children}
    </section>
  );
}

// ─── City Autocomplete ────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || "";

function CityAutocomplete({ value, onChange }) {
  const [query, setQuery] = useState(value?.nama_kabkot ? `${value.nama_kabkot}, ${value.nama_provinsi}` : "");
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/kecamatan/cities`)
      .then((r) => r.json())
      .then((d) => setCities(d));
  }, []);

  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filtered = query.length >= 2
    ? cities.filter((c) =>
        `${c.nama_provinsi} ${c.nama_kabkot}`.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8)
    : [];

  function select(city) {
    const display = `${city.nama_kabkot}, ${city.nama_provinsi}`;
    setQuery(display);
    onChange({ kode_kabkot: city.kode_kabkot, nama_kabkot: city.nama_kabkot, nama_provinsi: city.nama_provinsi });
    setOpen(false);
  }

  function handleInput(e) {
    setQuery(e.target.value);
    onChange(null);
    setOpen(true);
  }

  return (
    <div ref={ref} className="relative">
      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5f6368] pointer-events-none" />
      <input
        value={query}
        onChange={handleInput}
        onFocus={() => query && setOpen(true)}
        placeholder="Cari kota / kabupaten..."
        style={{ outline: "none" }}
        onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
        onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
        className="w-full pl-10 pr-4 py-3 border border-[#c0c8ca] rounded-xl text-sm text-[#191c1d] placeholder-[#71787b] bg-white transition-colors"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white rounded-2xl shadow-[0_12px_32px_rgba(25,28,29,0.06)] border border-[#c0c8ca] overflow-y-auto max-h-52">
          {filtered.map((c) => (
            <button key={c.kode_kabkot} onMouseDown={() => select(c)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[#f3f4f5] text-left transition-colors border-b border-[#edeeef] last:border-0">
              <MapPin className="w-3.5 h-3.5 text-[#5f6368] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#191c1d]">{c.nama_kabkot}</p>
                <p className="text-xs text-[#5f6368] mt-0.5">{c.nama_provinsi}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Business Type ────────────────────────────────────────────────────────────

function BizTypeSelector({ category, subtype, onCategory, onSubtype }) {
  const [categories, setCategories] = useState([]);
  const [subSearch, setSubSearch] = useState("");

  function sortAlpha(arr, keyFn = (x) => x) {
    return [...arr].sort((a, b) => {
      const ka = keyFn(a).toLowerCase();
      const kb = keyFn(b).toLowerCase();
      if (ka.startsWith("other") && !kb.startsWith("other")) return 1;
      if (!ka.startsWith("other") && kb.startsWith("other")) return -1;
      return ka.localeCompare(kb);
    });
  }

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || ""}/api/categories`)
      .then((r) => r.json())
      .then((d) => {
        const cats = sortAlpha(d.categories, (c) => c.name || c.id || "").map((c) => ({
          ...c,
          subcategories: c.subcategories ? sortAlpha(c.subcategories) : [],
        }));
        setCategories(cats);
      });
  }, []);

  useEffect(() => { setSubSearch(""); }, [category]);

  const active   = categories.find((c) => c.id === category);
  const filtered = active
    ? active.subcategories.filter((s) => s.toLowerCase().includes(subSearch.toLowerCase()))
    : [];
  const isCustom = active && subtype && !active.subcategories.includes(subtype);

  if (categories.length === 0) {
    return <div className="h-9 flex items-center"><span className="text-xs text-[#71787b]">Memuat kategori...</span></div>;
  }

  return (
    <div>
      {/* The business type is locked to the profile once onboarding completes —
          city access and scoring are tied to it, so say so up front. */}
      <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
        <p className="text-[11px] leading-relaxed text-amber-800">
          <b>Pilih dengan hati-hati — ini tidak bisa diubah nanti.</b> Akses kota dan skor Anda
          terikat pada jenis bisnis ini. Bisnis yang berbeda (misalnya, showroom mobil selain F&amp;B)
          memerlukan profil terpisah sendiri.
        </p>
      </div>

      {/* Horizontal scroll — category chips */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => {
          const Icon = ICON_MAP[cat.icon] ?? HelpCircle;
          const sel  = category === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => { onCategory(cat.id); onSubtype(""); }}
              style={sel ? { background: PRIMARY, borderColor: PRIMARY } : {}}
              className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full border shrink-0 text-xs font-medium transition-all ${
                sel
                  ? "text-white border-transparent"
                  : "border-[#c0c8ca] bg-white text-[#40484a] hover:bg-[#f3f4f5]"
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="whitespace-nowrap">{cat.name}</span>
            </button>
          );
        })}
      </div>

      {/* Subcategory panel */}
      {active && (
        <div className="mt-3 rounded-2xl border border-[#c0c8ca] overflow-hidden bg-white">

          {/* Panel header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#f3f4f5] border-b border-[#edeeef]">
            <div className="flex items-center gap-2 min-w-0">
              {(() => {
                const Icon = ICON_MAP[active.icon] ?? HelpCircle;
                return <Icon className="w-3.5 h-3.5 text-[#71787b] shrink-0" />;
              })()}
              <span className="text-xs font-medium text-[#191c1d]">{active.name}</span>
              {subtype && !isCustom && (
                <>
                  <span className="text-[#c0c8ca] text-xs">·</span>
                  <span
                    className="text-xs font-medium truncate max-w-[140px]"
                    style={{ color: ACCENT }}
                  >
                    {subtype}
                  </span>
                </>
              )}
              {isCustom && (
                <>
                  <span className="text-[#c0c8ca] text-xs">·</span>
                  <span className="text-xs font-medium text-[#5f6368] truncate max-w-[140px] italic">
                    {subtype}
                  </span>
                </>
              )}
            </div>

            {active.subcategories.length > 10 && (
              <div className="relative shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#71787b] pointer-events-none" />
                <input
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  placeholder="Cari..."
                  style={{ outline: "none" }}
                  onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
                  className="w-24 pl-7 pr-2.5 py-1 text-xs border border-[#c0c8ca] rounded-full bg-white transition-colors"
                />
              </div>
            )}
          </div>

          {/* Subcategory chips — scrollable if overflow */}
          <div className="p-4 flex flex-wrap gap-2 max-h-[188px] overflow-y-auto scrollbar-hide">
            {filtered.length > 0 ? (
              filtered.map((sub) => {
                const sel = subtype === sub;
                return (
                  <button
                    key={sub}
                    onClick={() => onSubtype(sel ? "" : sub)}
                    style={sel ? { background: PRIMARY, borderColor: PRIMARY, color: "#fff" } : {}}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      sel ? "border-transparent" : "border-[#c0c8ca] bg-white text-[#40484a] hover:bg-[#f3f4f5]"
                    }`}
                  >
                    {sub}
                  </button>
                );
              })
            ) : (
              <p className="text-xs text-[#71787b] py-1">
                Tidak ada hasil untuk &ldquo;{subSearch}&rdquo;
              </p>
            )}
          </div>

          {/* Custom input */}
          {active.allow_custom_input && (
            <div className="flex items-center gap-2.5 px-4 pb-4 pt-3 border-t border-[#edeeef]">
              <span className="text-[11px] font-medium text-[#5f6368] shrink-0">Tipe khusus:</span>
              <input
                value={isCustom ? subtype : ""}
                onChange={(e) => onSubtype(e.target.value)}
                placeholder={`misalnya, tipe ${active.name.toLowerCase()} unik saya`}
                style={{ outline: "none" }}
                onFocusCapture={(e) => (e.target.style.borderBottomColor = ACCENT)}
                onBlurCapture={(e) => (e.target.style.borderBottomColor = "#c0c8ca")}
                className="flex-1 text-xs py-1 border-b border-[#c0c8ca] bg-transparent text-[#191c1d] placeholder-[#9aa0a6] transition-colors"
              />
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ─── Demographic Selector ─────────────────────────────────────────────────────

const DEMO_PRESETS = [
  { id: "young",         label: "Dewasa Muda", desc: "Gen Z & Milenial",           values: { "Male|<18": 2, "Male|18–24": 22, "Male|25–34": 18, "Male|35–44": 3, "Male|45+": 2, "Female|<18": 2, "Female|18–24": 22, "Female|25–34": 18, "Female|35–44": 8, "Female|45+": 3 } },
  { id: "professionals", label: "Profesional", desc: "Pekerja dewasa 25–44",       values: { "Male|<18": 0, "Male|18–24": 8, "Male|25–34": 22, "Male|35–44": 18, "Male|45+": 5, "Female|<18": 0, "Female|18–24": 8, "Female|25–34": 22, "Female|35–44": 12, "Female|45+": 5 } },
  { id: "families",      label: "Keluarga",    desc: "Orang tua dengan anak",      values: { "Male|<18": 8, "Male|18–24": 3, "Male|25–34": 12, "Male|35–44": 15, "Male|45+": 8, "Female|<18": 8, "Female|18–24": 3, "Female|25–34": 15, "Female|35–44": 18, "Female|45+": 10 } },
  { id: "seniors",       label: "Lansia",      desc: "45 tahun ke atas",           values: { "Male|<18": 2, "Male|18–24": 3, "Male|25–34": 5, "Male|35–44": 10, "Male|45+": 25, "Female|<18": 2, "Female|18–24": 3, "Female|25–34": 5, "Female|35–44": 10, "Female|45+": 35 } },
  { id: "all",           label: "Semua Usia",  desc: "Audiens campuran luas",      values: { "Male|<18": 5, "Male|18–24": 10, "Male|25–34": 10, "Male|35–44": 10, "Male|45+": 5, "Female|<18": 5, "Female|18–24": 10, "Female|25–34": 15, "Female|35–44": 10, "Female|45+": 5 } },
];

const GENDER_COLORS = { Male: "#104230", Female: "#1d9160" };
const AGE_GROUPS    = ["<18", "18–24", "25–34", "35–44", "45+"];

function DemographicSelector({ value, onChange }) {
  const [custom, setCustom] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  function applyPreset(p) {
    setActivePreset(p.id);
    setCustom(false);
    onChange(p.values);
  }

  const barSegs = ["Male", "Female"].flatMap((g) =>
    AGE_GROUPS.map((a) => ({ key: `${g}|${a}`, g, pct: value[`${g}|${a}`] ?? 0 }))
  ).filter((s) => s.pct > 0);

  return (
    <div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {DEMO_PRESETS.map((p) => {
          const sel = activePreset === p.id && !custom;
          return (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              style={sel ? { background: PRIMARY, borderColor: PRIMARY } : {}}
              className={`flex flex-col items-start gap-0.5 px-3.5 py-3 rounded-xl text-left transition-all border ${
                sel ? "border-transparent" : "border-[#c0c8ca] bg-white hover:bg-[#f3f4f5]"
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`text-xs font-medium ${sel ? "text-white" : "text-[#191c1d]"}`}>{p.label}</span>
                {sel && <Check className="w-3 h-3 text-white/70" />}
              </div>
              <span className={`text-[10px] ${sel ? "text-white/60" : "text-[#5f6368]"}`}>{p.desc}</span>
            </button>
          );
        })}
        <button
          onClick={() => { setCustom(true); setActivePreset(null); }}
          style={custom ? { background: PRIMARY, borderColor: PRIMARY } : {}}
          className={`flex flex-col items-start gap-0.5 px-3.5 py-3 rounded-xl text-left transition-all border ${
            custom ? "border-transparent" : "border-[#c0c8ca] bg-white hover:bg-[#f3f4f5]"
          }`}
        >
          <div className="flex items-center justify-between w-full">
            <span className={`text-xs font-medium ${custom ? "text-white" : "text-[#191c1d]"}`}>Kustom</span>
            <SlidersHorizontal className={`w-3 h-3 ${custom ? "text-white/70" : "text-[#5f6368]"}`} />
          </div>
          <span className={`text-[10px] ${custom ? "text-white/60" : "text-[#5f6368]"}`}>Atur manual</span>
        </button>
      </div>

      <div className="flex rounded-full overflow-hidden h-1.5 mb-2 bg-[#edeeef]">
        {barSegs.map(({ key, g, pct }) => (
          <div key={key}
            style={{ width: `${pct}%`, backgroundColor: GENDER_COLORS[g], opacity: 0.85 }}
            className="transition-all duration-150" />
        ))}
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {["Male", "Female"].map((g) => (
            <div key={g} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GENDER_COLORS[g] }} />
              <span className="text-[11px] text-[#5f6368]">
                {g} {["Male", "Female"].flatMap((gg) => AGE_GROUPS.map((a) => `${gg}|${a}`))
                  .filter((k) => k.startsWith(g))
                  .reduce((s, k) => s + (value[k] ?? 0), 0)}%
              </span>
            </div>
          ))}
        </div>
        <span className="text-[11px] font-medium" style={{ color: ACCENT }}>
          {Object.values(value).reduce((s, v) => s + v, 0)}% total
        </span>
      </div>

      {custom && (
        <div className="border-t border-[#edeeef] pt-4">
          {["Male", "Female"].map((gender) => (
            <div key={gender} className="mb-4">
              <p className="text-[10px] font-medium uppercase tracking-widest mb-2.5"
                style={{ color: GENDER_COLORS[gender] }}>{gender}</p>
              {AGE_GROUPS.map((age) => {
                const key = `${gender}|${age}`;
                const pct = value[key] ?? 0;
                return (
                  <div key={key} className="flex items-center gap-3 mb-2">
                    <span className="text-[11px] text-[#5f6368] w-10 shrink-0">{age}</span>
                    <input type="range" min={0} max={100} value={pct}
                      onChange={(e) => onChange(redistribute(value, key, +e.target.value))}
                      className="flex-1 cursor-pointer h-1"
                      style={{ accentColor: GENDER_COLORS[gender] }} />
                    <span className="text-[11px] font-medium text-[#3c4043] w-7 text-right tabular-nums">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Slide 1 ──────────────────────────────────────────────────────────────────

// Purely the TARGET CUSTOMER'S income tier — price lives in Trade Areas as real
// rupiah, so the two are no longer conflated (a cheap product can target a
// wealthy area and vice versa). Maps to target_ses, by far the strongest lever
// in the score (ses_modifier spans 0.4–1.05, vs ±15% for the whole affinity
// layer), so it must never be dropped.
const MARKET_TIERS = [
  "Low income",
  "Lower-middle income",
  "Middle income",
  "Upper-middle income",
  "High income",
];

function Slide1({ form, set, onNext }) {
  const [q, setQ] = useState(0);
  const canProceed = form.businessName.trim() && form.bizCategory;
  const total = 2;

  function nextQuestion() {
    setQ((prev) => Math.min(total - 1, prev + 1));
  }

  function prevQuestion() {
    setQ((prev) => Math.max(0, prev - 1));
  }

  const nextDisabled =
    (q === 0 && !form.businessName.trim()) ||
    (q === 1 && !form.bizCategory);

  useEnterKey(() => {
    if (nextDisabled) return;
    if (q < total - 1) nextQuestion();
    else if (canProceed) onNext();
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="h-1.5 w-full rounded-full bg-[#e1e3e4]">
        <div
          className="h-1.5 rounded-full bg-[#0c3124] transition-all duration-300"
          style={{ width: `${((q + 1) / total) * 100}%` }}
        />
      </div>

      {q === 0 && (
        <StepBlock>
          <Label hint="Digunakan untuk mempersonalisasi laporan analisis Anda dan mengenali brand Anda di semua hasil skoring.">
            Nama Bisnis Anda
          </Label>
          <input
            value={form.businessName}
            onChange={(e) => set("businessName", e.target.value)}
            placeholder="misalnya, Warung Barokah, Kopi Kenangan..."
            style={{ outline: "none" }}
            onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
            onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
            className="w-full py-3 border-b-2 border-[#c0c8ca] text-lg font-medium text-[#191c1d] placeholder-[#71787b] bg-transparent transition-colors"
          />
        </StepBlock>
      )}

      {q === 1 && (
        <StepBlock>
          <Label mode="single" hint="Menentukan lapisan POI mana yang diaktifkan — jenis kompetitor, pola permintaan, dan filter SES semuanya dikalibrasi berdasarkan ini. Kedai kopi dan klinik menggunakan model skoring yang sama sekali berbeda.">
            Apa jenis bisnis yang akan Anda buka?
          </Label>
          <BizTypeSelector
            category={form.bizCategory}
            subtype={form.bizSubtype}
            onCategory={(v) => set("bizCategory", v)}
            onSubtype={(v) => set("bizSubtype", v)}
          />
        </StepBlock>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={prevQuestion}
          disabled={q === 0}
          className="rounded-lg px-4 py-2 text-sm font-medium text-[#40484a] transition-colors hover:bg-[#f3f4f5] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Sebelumnya
        </button>
        {q < total - 1 ? (
          <button
            type="button"
            onClick={nextQuestion}
            disabled={nextDisabled}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjut
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjutkan
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Slide 2 ──────────────────────────────────────────────────────────────────
const BUDGETS          = ["Varies by location", "Under 5M", "5–15M", "15–30M", "30–100M"];
const PRIORITIES       = ["High foot traffic", "High street visibility", "High purchasing power", "Low competition", "Easy access & parking", "Near anchor tenants", "Delivery-friendly coverage"];
const ENVIRONMENTS     = ["Office district", "Residential area", "School / university area", "Shopping district", "Traditional market / Pasar", "Transit hub / Station area", "Tourism area", "Hospital / healthcare cluster", "Industrial area", "Highway / main road corridor"];
// Only conditions the scoring engine can actually detect (scoring_engine.
// _avoid_triggers). Six earlier options — too many competitors, traffic
// congestion, flood-prone, near waste facility/TPS, crime-prone, zoning —
// were removed 2026-07: nothing in the data could trigger them, so selecting
// them silently did nothing. Offering a choice that has no effect is worse
// than not offering it. Do not re-add without a data source behind it.
const AVOID_CONDITIONS = ["Low purchasing power", "Very low foot traffic", "Far from public transit", "Low parking availability", "Poor road / street access", "Hard truck/logistics access"];

function Slide2({ form, set, onNext }) {
  const [q, setQ] = useState(0);
  const canProceed = !!form.city?.kode_kabkot && !!form.rentalBudget && form.priorities.length > 0 && form.environments.length > 0 && form.avoidConditions.length > 0;
  const total = 5;

  function nextQuestion() {
    setQ((prev) => Math.min(total - 1, prev + 1));
  }

  function prevQuestion() {
    setQ((prev) => Math.max(0, prev - 1));
  }

  const nextDisabled =
    (q === 0 && !form.city?.kode_kabkot) ||
    (q === 1 && !form.rentalBudget) ||
    (q === 2 && form.priorities.length === 0) ||
    (q === 3 && form.environments.length === 0) ||
    (q === 4 && form.avoidConditions.length === 0);

  useEnterKey(() => {
    if (nextDisabled) return;
    if (q < total - 1) nextQuestion();
    else if (canProceed) onNext();
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="h-1.5 w-full rounded-full bg-[#e1e3e4]">
        <div
          className="h-1.5 rounded-full bg-[#0c3124] transition-all duration-300"
          style={{ width: `${((q + 1) / total) * 100}%` }}
        />
      </div>

      {q === 0 && (
        <StepBlock>
          <Label hint="Kota target Anda adalah titik acuan geografis untuk semua lapisan spasial — radius kompetitor, peta panas lalu lintas pejalan kaki, dan kepadatan POI semuanya diukur dari titik ini.">
            Area mana yang Anda targetkan?
          </Label>
          <CityAutocomplete value={form.city} onChange={(v) => set("city", v)} />
        </StepBlock>
      )}

      {q === 1 && (
        <StepBlock>
          <Label mode="single" hint="Area dengan biaya sewa rata-rata melebihi anggaran Anda otomatis diturunkan prioritasnya, agar sistem tidak merekomendasikan lokasi yang tidak layak secara komersial untuk skala Anda. (dalam Rupiah; 5M = Rp. 5.000.000)">
            Berapa anggaran sewa bulanan?
          </Label>
          <ChipGroup options={BUDGETS} value={form.rentalBudget} onChange={(v) => set("rentalBudget", v)} />
        </StepBlock>
      )}

      {q === 2 && (
        <StepBlock>
          <Label mode="multi" hint="Pilihan ini menjadi pengganda berbobot dalam skor lokasi Anda. Memilih 'High foot traffic' meningkatkan skor area dengan kepadatan pejalan kaki tinggi — meskipun kompetisi di area itu juga tinggi.">
            Apa yang paling penting untuk bisnis Anda?
          </Label>
          <ChipGroup options={PRIORITIES} value={form.priorities} onChange={(v) => set("priorities", v)} multi />
        </StepBlock>
      )}

      {q === 3 && (
        <StepBlock>
          <Label mode="multi" hint="Area yang sesuai mendapat kenaikan skor. Distrik perkantoran menguntungkan layanan B2B; zona sekolah menguntungkan bimbel atau kios makanan. Pilih semua lingkungan yang relevan dengan pelanggan target Anda.">
            Jenis lingkungan yang disukai?
          </Label>
          <ChipGroup options={ENVIRONMENTS} value={form.environments} onChange={(v) => set("environments", v)} multi />
        </StepBlock>
      )}

      {q === 4 && (
        <div className="rounded-2xl p-5 border border-[#ffdad6] bg-[#fff8f7]">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#ba1a1a] shrink-0" />
            <p className="text-[10px] font-medium tracking-[0.08em] uppercase text-[#ba1a1a]">
              Filter Skor Negatif
            </p>
          </div>
          <p className="text-[11px] text-[#ba1a1a]/80 mb-4 leading-relaxed">
            Setiap kondisi yang dipilih secara aktif menurunkan skor area di mana risiko tersebut terdeteksi. Pilih dengan spesifik — memilih 'Terlalu banyak kompetitor' akan menyaring area yang sudah jenuh, meskipun lalu lintas pejalan kakinya tinggi.
          </p>
          <ChipGroup
            options={AVOID_CONDITIONS}
            value={form.avoidConditions}
            onChange={(v) => set("avoidConditions", v)}
            multi
            danger
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={prevQuestion}
          disabled={q === 0}
          className="rounded-lg px-4 py-2 text-sm font-medium text-[#40484a] transition-colors hover:bg-[#f3f4f5] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Sebelumnya
        </button>
        {q < total - 1 ? (
          <button
            type="button"
            onClick={nextQuestion}
            disabled={nextDisabled}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjut
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjutkan
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Slide 3 ──────────────────────────────────────────────────────────────────

const CUSTOMERS  = ["Students", "Office workers", "Young adults / Gen Z", "Families", "Stay-at-home parents", "Senior citizens", "Tourists", "Commuters / transit users", "MSMEs / business owners", "High-income professionals"];
const BUSY_TIMES = ["Morning rush (6–10am)", "Lunch hours (11am–2pm)", "Afternoon (2–5pm)", "Evening / dinner (5–9pm)", "Late night (9pm+)", "Weekend-focused", "All-day / consistent"];

function Slide3({ form, set, onNext }) {
  const [q, setQ] = useState(0);
  const canProceed = form.primaryCustomers.length > 0 && form.marketTier && form.busyTimes.length > 0;
  const total = 4;

  function nextQuestion() {
    setQ((prev) => Math.min(total - 1, prev + 1));
  }

  function prevQuestion() {
    setQ((prev) => Math.max(0, prev - 1));
  }

  const nextDisabled =
    (q === 0 && form.primaryCustomers.length === 0) ||
    (q === 2 && !form.marketTier) ||
    (q === 3 && form.busyTimes.length === 0);

  useEnterKey(() => {
    if (nextDisabled) return;
    if (q < total - 1) nextQuestion();
    else if (canProceed) onNext();
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="h-1.5 w-full rounded-full bg-[#e1e3e4]">
        <div
          className="h-1.5 rounded-full bg-[#0c3124] transition-all duration-300"
          style={{ width: `${((q + 1) / total) * 100}%` }}
        />
      </div>

      {q === 0 && (
        <StepBlock>
          <Label mode="multi" hint="Menentukan klaster demografis mana yang diprioritaskan dalam skoring spasial. Memilih 'Office workers' meningkatkan skor area dekat distrik komersial; 'Students' mengaktifkan kedekatan dengan universitas dan sekolah.">
            Siapa pelanggan utama Anda?
          </Label>
          <ChipGroup options={CUSTOMERS} value={form.primaryCustomers} onChange={(v) => set("primaryCustomers", v)} multi />
        </StepBlock>
      )}

      {q === 1 && (
        <StepBlock>
          <Label hint="Dicocokkan dengan distribusi demografis aktual tiap area. Semakin dekat komposisi usia-gender area dengan profil Anda, semakin tinggi skor kecocokan demografisnya.">
            Sebaran usia & gender pasar target?
          </Label>
          <DemographicSelector value={form.ageGroups} onChange={(v) => set("ageGroups", v)} />
        </StepBlock>
      )}

      {q === 2 && (
        <StepBlock>
          <Label mode="single" hint="Dicocokkan dengan daya beli tiap area — menargetkan pelanggan berpenghasilan tinggi di area berpenghasilan rendah menurunkan skor kelayakan. Ini adalah personalisasi terkuat di peta Anda. Harga produk Anda sendiri ditanyakan terpisah di bagian Area Dagang.">
            Anda menjual ke tingkat pendapatan yang mana?
          </Label>
          <ChipGroup options={MARKET_TIERS} value={form.marketTier} onChange={(v) => set("marketTier", v)} />
        </StepBlock>
      )}

      {q === 3 && (
        <StepBlock>
          <Label mode="multi" hint="Menentukan lapisan lalu lintas pejalan kaki berbasis waktu mana yang diberi bobot. Bisnis 'Morning' diskor terhadap pola commuter pagi hari; 'Evening' terhadap data mobilitas nightlife dan jam makan malam.">
            Kapan bisnis Anda paling ramai?
          </Label>
          <ChipGroup options={BUSY_TIMES} value={form.busyTimes} onChange={(v) => set("busyTimes", v)} multi />
        </StepBlock>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={prevQuestion}
          disabled={q === 0}
          className="rounded-lg px-4 py-2 text-sm font-medium text-[#40484a] transition-colors hover:bg-[#f3f4f5] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Sebelumnya
        </button>
        {q < total - 1 ? (
          <button
            type="button"
            onClick={nextQuestion}
            disabled={nextDisabled}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjut
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjut
          </button>
        )}
      </div>
    </div>
  );
}

const INCOME_TO_SES = {
  "Low income":          "low_income",
  "Lower-middle income": "low_middle",
  "Middle income":       "middle_income",
  "Upper-middle income": "upper_income",
  "High income":         "upper_income",
};

// ─── Slide 4: Trade Areas ─────────────────────────────────────────────────────
// The revenue side of the profile: what one customer is worth, and how many of
// them you expect to win. Be clear about what these do and do NOT do — basket
// size and frequency are the SAME multiplier for every cell on the map, so they
// turn a score into rupiah but never reorder anything. The only field here that
// could move the ranking is price, and it does so indirectly via the income tier
// asked in the previous step. Presented as a projection, never as a location
// signal.

const FREQ_UNITS = ["per week", "per month", "per year"];

function RupiahInput({ value, onChange, placeholder }) {
  const shown = value === "" || value == null ? "" : Number(value).toLocaleString("id-ID");
  return (
    <div className="flex items-baseline gap-2 w-full">
      <span className="shrink-0 text-sm font-medium text-[#71787b]">Rp</span>
      <input
        inputMode="numeric"
        value={shown}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "");
          onChange(digits === "" ? "" : Number(digits));
        }}
        placeholder={placeholder}
        style={{ outline: "none" }}
        onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
        onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
        className="w-full border-b-2 border-[#c0c8ca] bg-transparent py-3 text-lg font-medium text-[#191c1d] placeholder-[#71787b] transition-colors"
      />
    </div>
  );
}

function SlideTradeAreas({ form, set, onNext }) {
  const [q, setQ] = useState(0);
  const total = 4;

  const priceOk =
    form.productPriceMin !== "" && form.productPriceMax !== "" &&
    Number(form.productPriceMax) >= Number(form.productPriceMin);
  const canProceed = priceOk && form.basketSize !== "" && form.buyingFrequency !== "";

  function nextQuestion() { setQ((p) => Math.min(total - 1, p + 1)); }
  function prevQuestion() { setQ((p) => Math.max(0, p - 1)); }

  const nextDisabled =
    (q === 0 && !priceOk) ||
    (q === 1 && form.basketSize === "") ||
    (q === 2 && form.buyingFrequency === "");

  useEnterKey(() => {
    if (nextDisabled) return;
    if (q < total - 1) nextQuestion();
    else if (canProceed) onNext();
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="h-1.5 w-full rounded-full bg-[#e1e3e4]">
        <div
          className="h-1.5 rounded-full bg-[#0c3124] transition-all duration-300"
          style={{ width: `${((q + 1) / total) * 100}%` }}
        />
      </div>

      {q === 0 && (
        <StepBlock>
          <Label hint="Barang termurah dan termahal yang Anda jual. Digunakan untuk menilai apakah harga Anda sesuai dengan daya beli suatu area — bukan untuk memberi peringkat lokasi secara langsung.">
            Berapa rentang harga produk Anda?
          </Label>
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-1 text-xs font-medium text-[#71787b]">Harga terendah</p>
              <RupiahInput value={form.productPriceMin} onChange={(v) => set("productPriceMin", v)} placeholder="500.000" />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-[#71787b]">Harga tertinggi</p>
              <RupiahInput value={form.productPriceMax} onChange={(v) => set("productPriceMax", v)} placeholder="50.000.000" />
            </div>
            {form.productPriceMin !== "" && form.productPriceMax !== "" && !priceOk && (
              <p className="text-xs font-medium text-[#ba1a1a]">Harga tertinggi minimal harus sama dengan harga terendah.</p>
            )}
          </div>
        </StepBlock>
      )}

      {q === 1 && (
        <StepBlock>
          <Label hint="Rata-rata rupiah yang dibelanjakan satu pelanggan dalam satu kunjungan — bukan hari terbaik Anda. Digabungkan dengan frekuensi pembelian untuk memperkirakan nilai satu pelanggan per tahun.">
            Berapa rata-rata nilai belanja Anda?
          </Label>
          <RupiahInput value={form.basketSize} onChange={(v) => set("basketSize", v)} placeholder="10.000.000" />
        </StepBlock>
      )}

      {q === 2 && (
        <StepBlock>
          <Label hint="Seberapa sering pelanggan yang sama kembali. Minimarket mungkin beberapa kali per minggu; toko elektronik lebih dekat ke sekali per tahun.">
            Seberapa sering pelanggan membeli?
          </Label>
          <div className="flex items-end gap-3">
            <input
              inputMode="numeric"
              value={form.buyingFrequency}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                set("buyingFrequency", digits === "" ? "" : Number(digits));
              }}
              placeholder="1"
              style={{ outline: "none" }}
              onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
              onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
              className="w-20 border-b-2 border-[#c0c8ca] bg-transparent py-3 text-lg font-medium text-[#191c1d] placeholder-[#71787b] transition-colors"
            />
            <div className="flex flex-wrap gap-2 pb-2">
              {FREQ_UNITS.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => set("buyingFrequencyUnit", u)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    form.buyingFrequencyUnit === u
                      ? "border-brand-500 bg-teal-50 text-brand-700"
                      : "border-[#c0c8ca] text-[#40484a] hover:bg-[#f3f4f5]"
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
        </StepBlock>
      )}

      {q === 3 && (
        <StepBlock>
          <Label hint="Opsional. Perkiraan Anda sendiri tentang porsi orang di sekitar yang bisa Anda dapatkan. Kami membandingkannya dengan apa yang didukung data area tersebut, agar Anda tahu apakah ini realistis.">
            Berapa pangsa pasar yang Anda harapkan? (opsional)
          </Label>
          <div className="flex items-baseline gap-2">
            <input
              inputMode="decimal"
              value={form.marketExpectation}
              onChange={(e) => {
                const clean = e.target.value.replace(/[^\d.]/g, "");
                set("marketExpectation", clean);
              }}
              placeholder="10"
              style={{ outline: "none" }}
              onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
              onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
              className="w-24 border-b-2 border-[#c0c8ca] bg-transparent py-3 text-lg font-medium text-[#191c1d] placeholder-[#71787b] transition-colors"
            />
            <span className="text-sm font-medium text-[#71787b]">%</span>
          </div>
        </StepBlock>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={prevQuestion}
          disabled={q === 0}
          className="rounded-lg px-4 py-2 text-sm font-medium text-[#40484a] transition-colors hover:bg-[#f3f4f5] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Sebelumnya
        </button>
        {q < total - 1 ? (
          <button
            type="button"
            onClick={nextQuestion}
            disabled={nextDisabled}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjut →
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Lanjutkan →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Slide 5: Market Anchors ──────────────────────────────────────────────────

const REFERENCE_TYPES = [
  { id: "Direct Competitor",   desc: "Menjual produk atau layanan yang sama ke pelanggan target yang sama." },
  { id: "Indirect Competitor", desc: "Memenuhi kebutuhan yang sama tapi dengan solusi atau format berbeda." },
  { id: "Aspirational",        desc: "Brand yang ingin Anda tiru atau jadikan tolok ukur performa." },
  { id: "Strategic",           desc: "Bukan rival langsung, tapi penting bagi positioning atau strategi pasar Anda." },
  { id: "Complementary",       desc: "Melengkapi penawaran Anda — pelanggan mengunjungi keduanya, bukan salah satu." },
  { id: "Preference",          desc: "Alternatif konsumen yang pelanggannya berpotensi menjadi pelanggan Anda." },
];

function ReferenceTypePicker({ value, onChange }) {
  const [hovered, setHovered] = useState(null);
  const active = REFERENCE_TYPES.find((t) => t.id === (hovered || value));
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold text-[#40484a] uppercase tracking-widest">Tipe Referensi</span>
      <div className="flex flex-wrap gap-2">
        {REFERENCE_TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            onMouseEnter={() => setHovered(t.id)}
            onMouseLeave={() => setHovered(null)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${value === t.id ? "border-brand-500 bg-teal-50 text-brand-700" : "border-[#c0c8ca] text-[#40484a] hover:bg-[#f3f4f5]"}`}
          >
            {idLabel(t.id)}
          </button>
        ))}
      </div>
      {active && (
        <p className="text-[11px] text-[#71787b] leading-relaxed min-h-[1.5em]">{active.desc}</p>
      )}
    </div>
  );
}

function Slide4({ form, set, onNext }) {
  const benchmarks = form.benchmarks;

  function setBenchmark(i, key, val) {
    const next = benchmarks.map((b, idx) => idx === i ? { ...b, [key]: val } : b);
    set("benchmarks", next);
  }

  const filledCount = benchmarks.filter((b) => b.name.trim() && b.type).length;
  const canProceed  = filledCount >= 1;

  useEnterKey(() => { if (canProceed) onNext(); });

  return (
    <div className="flex flex-col gap-6">
      <StepBlock>
        <Label hint="Bisnis yang keberadaannya di sekitar menandakan permintaan kuat atau pasar yang terbukti — misalnya, Indomaret terdekat mengonfirmasi lalu lintas pejalan kaki; McDonald's mengonfirmasi pasar makanan sudah ada.">
          Siapa acuan pasar Anda?
        </Label>
        <div className="mt-3 mb-1 rounded-lg border border-[#e1e3e4] bg-[#f8f9fa] overflow-hidden">
          {REFERENCE_TYPES.map((t, i) => (
            <div key={t.id} className={`flex gap-3 px-3.5 py-2 ${i < REFERENCE_TYPES.length - 1 ? "border-b border-[#e1e3e4]" : ""}`}>
              <span className="text-[11px] font-semibold text-[#0c3124] w-[130px] shrink-0 pt-px">{idLabel(t.id)}</span>
              <span className="text-[11px] text-[#71787b] leading-relaxed">{t.desc}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-5 mt-2">
          {benchmarks.map((b, i) => {
            const active = i === 0 || benchmarks[i - 1].name.trim();
            return (
              <div key={i} className={`flex flex-col gap-3 rounded-xl border p-4 transition-opacity ${active ? "border-[#c0c8ca] bg-white" : "border-[#e8eaeb] bg-[#f8f9fa] opacity-40 pointer-events-none"}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0c3124] text-xs font-bold text-white">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs font-semibold text-[#40484a] uppercase tracking-widest">Nama Bisnis</span>
                </div>
                <input
                  value={b.name}
                  onChange={(e) => setBenchmark(i, "name", e.target.value)}
                  placeholder={i === 0 ? "misalnya, Starbucks, Alfamart" : `Kompetitor ${i + 1}`}
                  style={{ outline: "none" }}
                  onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlurCapture={(e) => (e.target.style.borderColor = "#c0c8ca")}
                  className="w-full rounded-lg border border-[#c0c8ca] px-4 py-2.5 text-sm text-[#191c1d] placeholder-[#71787b] bg-transparent transition-colors"
                />
                {b.name.trim() && (
                  <ReferenceTypePicker value={b.type} onChange={(t) => setBenchmark(i, "type", t)} />
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[#71787b]">{filledCount} dari 5 ditambahkan — minimal 1 diperlukan</p>
      </StepBlock>

      <div className="flex items-center justify-between gap-3 pt-2">
        <div />
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Lanjut
        </button>
      </div>
    </div>
  );
}

// ─── Slide 5: POI Priority ────────────────────────────────────────────────────

// One option per DISTINCT signal. The previous 44 options collapsed to these
// same ~20 density attributes, so "Hospital", "Dental clinic" and "Pharmacy"
// returned an identical result — users spent their limited picks on duplicates
// while believing they were being precise. Grouped labels say plainly what the
// data can actually resolve. Keep in sync with scoring_engine._POI_ATTRS.
const POI_OPTIONS = [
  // Transport
  "Bus stop / Halte", "Train / MRT / Bus terminal",
  // Education
  "School / Kindergarten", "University / Campus",
  // Health
  "Hospital / Clinic / Pharmacy",
  // F&B
  "Restaurant / Cafe / F&B", "Traditional Market",
  // Retail
  "Mall / Supermarket", "Minimarket / Retail shops",
  // Services
  "Salon / Barbershop", "Laundry / Daily services", "Bank / ATM", "Gas Station",
  // Office & Residential
  "Office / Co-working", "Apartment / Housing complex",
  // Entertainment & Leisure
  "Cinema / Karaoke", "Gym / Sports center", "Hotel", "Park / Green space",
  // Religious & Government
  "Place of worship", "Government / Post office",
];

function Slide5({ form, set, onNext, maxPoiPriorities }) {
  const selected = form.poiPriorities;
  const canProceed = selected.length >= 1;

  function toggle(poi) {
    if (selected.includes(poi)) {
      set("poiPriorities", selected.filter((p) => p !== poi));
    } else if (selected.length < maxPoiPriorities) {
      set("poiPriorities", [...selected, poi]);
    }
  }

  useEnterKey(() => { if (canProceed) onNext(); });

  return (
    <div className="flex flex-col gap-6">
      <StepBlock>
        <Label mode="multi" hint={`Pilih hingga ${maxPoiPriorities} kategori Point of Interest untuk diprioritaskan dalam analisis lokasi Anda. Area yang cocok akan meningkatkan skor Anda berdasarkan tingkat kepentingan strategisnya bagi model bisnis Anda.`}>
          Kategori POI mana yang paling penting?
        </Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {POI_OPTIONS.map((poi) => {
            const sel = selected.includes(poi);
            const maxed = selected.length >= maxPoiPriorities && !sel;
            return (
              <button
                key={poi}
                type="button"
                onClick={() => toggle(poi)}
                disabled={maxed}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${sel ? "border-brand-500 bg-teal-50 text-brand-700" : "border-[#c0c8ca] text-[#40484a] hover:bg-[#f3f4f5] disabled:opacity-40 disabled:cursor-not-allowed"}`}
              >
                {idLabel(poi)}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[#71787b]">{selected.length} dari {maxPoiPriorities} dipilih</p>
      </StepBlock>

      <div className="flex items-center justify-between gap-3 pt-2">
        <div />
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Lanjut
        </button>
      </div>
    </div>
  );
}

// ─── Slide 6: Analysis Settings ───────────────────────────────────────────────

function Slide6({ form, set, onSubmit, loading, error }) {
  useEnterKey(() => { if (!loading) onSubmit(); });

  return (
    <div className="flex flex-col gap-6">
      <StepBlock>
        <Label mode="single" hint="Resolusi grid menentukan seberapa detail peta skor lokasi. 1 km memberikan gambaran seluruh kota secara cepat. 100 m memberikan presisi setingkat jalan dan memuat lebih banyak sel — terbaik untuk menelusuri area tertentu.">
          Seberapa detail grid analisis yang diinginkan?
        </Label>
        <ResolutionSelect value={form.gridResolution} onChange={(v) => set("gridResolution", v)} />
      </StepBlock>

      <StepBlock>
        <Label mode="single" hint="Jangkauan populasi menentukan seberapa jauh skor lokasi menghitung penduduk sebagai pasar potensial Anda. Sudah dihitung sebelumnya dari data kami (tanpa biaya tambahan) dan juga menggerakkan tampilan 'Population within X km' di Analisis Mendalam. Semakin luas = semakin banyak orang terhitung; 5 km adalah standar yang baik untuk sebagian besar ritel.">
          Seberapa jauh populasi harus dihitung?
        </Label>
        <CatchmentSelect value={form.populationCatchment} onChange={(v) => set("populationCatchment", v)} />
      </StepBlock>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <div />
        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {loading ? "Membuat..." : "Mulai Analisis →"}
        </button>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const SLIDES = [
  { title: "Tentang\nBisnis Anda.",      sub: "Bantu kami memahami model bisnis dan kebutuhan operasional Anda." },
  { title: "Tentang\nLokasi.",           sub: "Persempit ke geografi terbaik dan saring area bermasalah." },
  { title: "Tentang Pasar\nTarget Anda.", sub: "Beri tahu kami siapa target pelanggan Anda — bagian paling krusial." },
  { title: "Area\nDagang.",              sub: "Berapa nilai satu pelanggan, agar kami bisa mengubah peta menjadi proyeksi penjualan." },
  { title: "Acuan\nPasar.",              sub: "Bisnis yang keberadaannya di sekitar menandakan pasar target Anda sudah ada." },
  { title: "Prioritas\nPOI.",            sub: "Pilih kategori point-of-interest yang paling penting untuk analisis lokasi Anda." },
  { title: "Pengaturan\nAnalisis.",      sub: "Atur presisi dan skala peta skor lokasi Anda." },
];

const STEP_LABELS = ["Bisnis", "Lokasi", "Pasar", "Area Dagang", "Acuan", "POI", "Analisis"];

export default function Onboarding() {
  const navigate    = useNavigate();
  const { setUser } = useAuth();
  const [slide, setSlide]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [maxPoiPriorities, setMaxPoiPriorities] = useState(5);
  const [submitError, setSubmitError] = useState(null);
  // Profile cap: checked up front so a user at the cap learns it before filling
  // seven steps, not from a button that silently does nothing at the end.
  const [slots, setSlots] = useState(null);
  const atCap = !!slots && slots.canCreate === false;

  useEffect(() => {
    getProfileSlots().then(setSlots).catch(() => {});
  }, []);

  useEffect(() => {
    getProfileLimitDefaults().then((l) => l?.max_poi_priorities && setMaxPoiPriorities(l.max_poi_priorities)).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    businessName: "",
    bizCategory: "",
    bizSubtype: "",
    priorities: [],
    city: "",
    gridResolution: "100m",
    populationCatchment: "5km",
    rentalBudget: "",
    environments: [],
    avoidConditions: [],
    primaryCustomers: [],
    ageGroups: initAgeGender(),
    marketTier: "",
    // Trade Areas — revenue projection inputs (see SlideTradeAreas)
    productPriceMin: "",
    productPriceMax: "",
    basketSize: "",
    buyingFrequency: "",
    buyingFrequencyUnit: "per month",
    marketExpectation: "",
    busyTimes: [],
    benchmarks: [
      { name: "", type: "Direct Competitor" },
      { name: "", type: "" },
      { name: "", type: "" },
      { name: "", type: "" },
      { name: "", type: "" },
    ],
    poiPriorities: [],
  });


  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById("root")];
    els.forEach((el) => {
      if (el) {
        el.style.overflow = "auto";
        el.style.height = "auto";
      }
    });
    return () => {
      els.forEach((el) => {
        if (el) {
          el.style.overflow = "";
          el.style.height = "";
        }
      });
    };
  }, []);

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleSubmit() {
    setLoading(true);
    setSubmitError(null);
    try {
      const profile = {
        ...form,
        target_ses: INCOME_TO_SES[form.marketTier] || "all",
        benchmarks: form.benchmarks.filter((b) => b.name.trim()),
      };
      // Persist to DB (also caches landhep_profile for synchronous readers).
      await createProfile(profile);
      // Hard reload: the new profile is now active, and every profile-scoped
      // context (owned grids, comparison bucket, Branch Radar) must re-init.
      window.location.href = "/analysis";
    } catch (e) {
      // Never fail silently: the button used to re-enable with no word on why.
      setSubmitError(e?.message || "Profil gagal disimpan. Coba lagi sebentar lagi.");
      getProfileSlots().then(setSlots).catch(() => {});
    } finally {
      setLoading(false);
    }
  }

  const { title } = SLIDES[slide - 1];

  return (
    <div className="min-h-screen bg-[#0c3124] text-[#191c1d] antialiased">
      <header className="fixed top-0 z-50 w-full border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="mx-auto flex h-20 w-full max-w-[1280px] items-center justify-between px-8">
          <span className="text-2xl font-extrabold tracking-tight text-brand-700">Landhep</span>
          <button
            onClick={async () => { try { await apiLogout(); } catch {} setUser(null); navigate("/"); }}
            className="rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-brand-800"
          >
            Keluar
          </button>
        </div>
      </header>

      <main className="relative min-h-screen px-6 pb-20 pt-32">
        <div className="absolute inset-0 opacity-35 mix-blend-screen">
          <img
            className="h-full w-full object-cover"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuAi2NbHIi9N6WW4WiZk8jo59GpJJ05okwa2XZIq9om2SRFf-P8Am4FHkUMbdXaDf458GvZ3hYrf_pDD_jrWi3oCC1d_veQB-eM1l7Y2AWPVnwGv7SC5mfP6Byrl7wlaH_ojzMo6I2AdGHxVVinXqi2-TSkYX7xIZ5JPPzVSwL-3rCPBN5nIaGe0zjtQlpUjg889cSxTw39yIPm7wf8E1Mc9eyzLvNHQ-htLmfCH3bYDF0peNMBkp4Dm3_N8rg8-c6RPIz3fkSak2Qg"
            alt="Pola peta latar belakang"
          />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,106,99,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(0,106,99,0.12)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,12,35,0.6),rgba(0,12,35,0.5))]" />

        <div className="relative mx-auto w-full max-w-6xl">
          <div className="mb-10 flex flex-col items-center">
            <div className="mb-4 flex items-center gap-2">
              {STEP_LABELS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-14 rounded-full ${i + 1 <= slide ? "bg-[#99ceb7]" : "bg-white/25"}`}
                />
              ))}
            </div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#99ceb7]">
              Langkah {slide} dari {STEP_LABELS.length}
            </p>
            <h1 className="text-center text-4xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
              {title.replace(/\n/g, " ")}
            </h1>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <aside className="lg:col-span-4">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-6 backdrop-blur-xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#99ceb7]/35 bg-[#104230]/30 px-3 py-1">
                  <Sparkles className="h-3.5 w-3.5 text-[#104230]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#99ceb7]">Asisten AI Strategis</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-200">{SLIDES[slide - 1].sub}</p>

                <div className="mt-6 space-y-3">
                  {STEP_LABELS.map((label, i) => {
                    const num = i + 1;
                    const done = num < slide;
                    const active = num === slide;
                    return (
                      <div key={label} className="flex items-center gap-2.5">
                        <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${done ? "bg-[#14543e] text-white" : active ? "bg-[#104230] text-[#99ceb7]" : "bg-white/20 text-slate-300"}`}>
                          {done ? <Check className="h-3 w-3" /> : num}
                        </div>
                        <span className={`text-xs font-semibold ${active ? "text-white" : "text-slate-300"}`}>{label}</span>
                      </div>
                    );
                  })}
                </div>

                {slide > 1 && (
                  <button
                    onClick={() => setSlide((s) => s - 1)}
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-[#99ceb7] transition-opacity hover:opacity-75"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke langkah sebelumnya
                  </button>
                )}
              </div>
            </aside>

            <section className="lg:col-span-8">
              <div className="rounded-2xl border border-[#c0c8ca]/20 bg-white p-7 shadow-[0_12px_32px_rgba(25,28,29,0.04)] md:p-10">
                {atCap ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><Lock className="h-5 w-5" /></div>
                    <h2 className="text-xl font-bold text-[#191c1d]">Batas profil bisnis tercapai</h2>
                    <p className="text-sm leading-relaxed text-[#40484a]">
                      Akun Anda sudah memakai {slots.used} dari {slots.max} profil bisnis. Setiap bisnis memerlukan profilnya sendiri,
                      jadi profil baru belum bisa dibuat. Minta admin Landhep menaikkan batas, atau lanjutkan dengan salah satu profil yang sudah ada.
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <button type="button" onClick={() => navigate("/analysis")}
                        className="rounded-lg bg-[#0c3124] px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90">
                        Kembali ke Analisis
                      </button>
                      <button type="button" onClick={() => navigate("/profile")}
                        className="rounded-lg border border-[#c0c8ca] px-5 py-2 text-sm font-semibold text-[#40484a] transition-colors hover:bg-[#f3f4f5]">
                        Kelola profil
                      </button>
                    </div>
                  </div>
                ) : <>
                {slide === 1 && <Slide1 form={form} set={set} onNext={() => setSlide(2)} />}
                {slide === 2 && <Slide2 form={form} set={set} onNext={() => setSlide(3)} />}
                {slide === 3 && <Slide3 form={form} set={set} onNext={() => setSlide(4)} />}
                {slide === 4 && <SlideTradeAreas form={form} set={set} onNext={() => setSlide(5)} />}
                {slide === 5 && <Slide4 form={form} set={set} onNext={() => setSlide(6)} />}
                {slide === 6 && <Slide5 form={form} set={set} onNext={() => setSlide(7)} maxPoiPriorities={maxPoiPriorities} />}
                {slide === 7 && <Slide6 form={form} set={set} onSubmit={handleSubmit} loading={loading} error={submitError} />}
                </>}
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
